import * as React from 'react';
import { Form, useFormApi } from 'informedv4';
import { TextBox } from '../../forms/informedv4/TextBoxV4';
import { Required } from '../../forms/validation/Required';
import AsyncActionButton from '../../misc/components/AsyncActionButton';
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import { AuditForm } from './AuditModal';
import { useSecurityContext } from '../../Core/SecurityContext';
import { TextArea } from '../../forms/informedv4/TextAreaV4';
import { useWebPortalLegacyClient } from '../../misc/api/WebPortalTokenClient';

export interface AuditModalBodyFormProps {
    isSubmitting: boolean;
    onClose: () => void;
    isAuditAnnotationDisabled?: boolean;
    annotationLabel?: string;
    submitLabel?: string;
    fetching?: boolean;
    onSubmit: (values: AuditForm) => void;
}

declare const rootUrl: string;

export const AuditModalBodyForm = ({ isSubmitting, onClose, isAuditAnnotationDisabled, annotationLabel, submitLabel, onSubmit, children }: React.PropsWithChildren<AuditModalBodyFormProps>) => {
    const [passwordFetching, setPasswordFetching] = React.useState(false);
    const { currentUser } = useSecurityContext();

    const formApi = useFormApi();
    const legacyClient = useWebPortalLegacyClient();

    const validatePassword = password => new Promise((resolve) => {
        legacyClient.validateAuditPassword(password)
            .then(json => {
                setPasswordFetching(false);
                if (json === true) resolve(null);
                else formApi.setError("Password", "The password entered is incorrect");
            });
    });

    const handleSubmit = async () => {
        let state = formApi.getFormState();

        setPasswordFetching(true);
        await validatePassword(state.values.Password);

        formApi.validate();
        state = formApi.getFormState();
        if (state.invalid)
            return;

        const annotationString = formApi.getValue("Annotation")?.toString() ?? "";
        const replacedString = annotationString.replace(/[\n\r]/g, ' ')
        formApi.setValue("Annotation", replacedString);
        onSubmit(state.values as unknown as AuditForm);
    }

    return (
        <ErrorBoundary>
            <div className="modal-body">
                {children}
                <div className="form-group">
                    <label htmlFor="userName">Username</label>
                    <input id="UserName" className="form-control" value={currentUser.Username} disabled />
                </div>

                {!isAuditAnnotationDisabled ? <div className="form-group">
                    <label htmlFor="AuditModal_Annotation">{annotationLabel || "Annotation"}</label>
                    <TextArea name="Annotation" id="AuditModal_Annotation" field="Annotation" validate={Required} validateOnBlur />
                </div> : null}

                <div className="form-group">
                    <label htmlFor="AuditModal_Password">Password</label>
                    <TextBox name="Password" id="AuditModal_Password" field="Password" type="password" validate={Required} validateOnBlur autoComplete="current-password" />
                </div>
            </div>

            <div className="modal-footer">
                <AsyncActionButton
                    id="audit-modal-save"
                    className="btn btn-primary"
                    type="submit"
                    isSubmitting={isSubmitting || passwordFetching}
                    onClick={handleSubmit}
                >
                    {submitLabel ? submitLabel : "Save"}
                </AsyncActionButton>
                <button id="audit-modal-cancel" className="btn btn-outline btn-primary" onClick={onClose}>Cancel</button>
            </div>
        </ErrorBoundary>
    );
}

export const AuditModalBody = (props: React.PropsWithChildren<AuditModalBodyFormProps>) => {
    return (
        <div>
            <Form id="auditForm">
                <AuditModalBodyForm  {...props} />
            </Form>
        </div>
    );
}
